import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Inbox, X } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { AuthContext } from '../App';

// PendingTarBanner — persistent, dismissible banner shown at the top of
// every authenticated page when there are TARs waiting to be received.
// Dismissal is per-session (sessionStorage) so it comes back on next
// login/reload — the goal is to be nudgy, not annoying forever.
const DISMISS_KEY = 'banner:tar_inbox_dismissed_at';
const DISMISS_TTL_MS = 6 * 60 * 60 * 1000; // 6h

export const PendingTarBanner = () => {
  const { counters } = useNotifications();
  const { user } = useContext(AuthContext);
  const pending = counters?.inbox_tar_pending || 0;
  // Users with TAR permissions confirm in /store-transfer; técnicos confirm
  // in their van page (/my-stock).
  const canUseStoreTransfer = user?.role === 'admin'
    || (Array.isArray(user?.permissions)
        && (user.permissions.includes('tar.create_from_own') || user.permissions.includes('tar.create_any')));
  const inboxPath = canUseStoreTransfer ? '/store-transfer' : '/my-stock';
  const [dismissedAt, setDismissedAt] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) || null; } catch { return null; }
  });

  // Auto-reset dismissal if the pending count grew since we last dismissed.
  const [dismissedAtCount, setDismissedAtCount] = useState(() => {
    try { return parseInt(sessionStorage.getItem(DISMISS_KEY + ':count') || '0', 10); } catch { return 0; }
  });

  useEffect(() => {
    if (!dismissedAt) return;
    if (pending > dismissedAtCount) {
      // new TARs arrived — re-show
      setDismissedAt(null);
      try { sessionStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
    }
  }, [pending, dismissedAt, dismissedAtCount]);

  const dismiss = () => {
    const now = new Date().toISOString();
    setDismissedAt(now);
    setDismissedAtCount(pending);
    try {
      sessionStorage.setItem(DISMISS_KEY, now);
      sessionStorage.setItem(DISMISS_KEY + ':count', String(pending));
    } catch { /* ignore */ }
  };

  const stillDismissed = dismissedAt
    && (Date.now() - new Date(dismissedAt).getTime()) < DISMISS_TTL_MS
    && pending <= dismissedAtCount;

  if (pending === 0 || stillDismissed) return null;

  return (
    <div
      className="sticky top-0 z-30 bg-amber-50 border-b border-amber-200 px-3 sm:px-6 py-2.5 flex items-center gap-3"
      data-testid="pending-tar-banner"
    >
      <Inbox className="h-4 w-4 text-amber-700 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs sm:text-sm text-amber-900 leading-tight">
          <strong>{pending}</strong> transferência{pending !== 1 ? 's' : ''} de stock a aguardar receção no teu armazém.{' '}
          <Link
            to={inboxPath}
            className="underline font-medium hover:text-amber-800"
            data-testid="pending-tar-banner-link"
          >
            Confirmar agora →
          </Link>
        </p>
      </div>
      <button
        onClick={dismiss}
        className="text-amber-700 hover:bg-amber-100 rounded p-1 flex-shrink-0"
        aria-label="Dispensar"
        data-testid="pending-tar-banner-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default PendingTarBanner;
